// Frontend/src/lib/notificationStreamBridge.ts
//
// The one place notificationStream.ts's parsed events turn into React Query
// cache writes. Imported once for its side effect (see main.tsx) -- ES
// modules are singletons, so importing it from more than one place would
// still only wire the subscription once.
//
// This is what makes SSE a *cache-freshness* mechanism rather than a UI
// rewrite: NotificationBell.tsx's existing "unread count rose -> toast +
// chime" effect, and the announcement pages' existing rendering, are
// unchanged -- they just react to cache data that now updates itself
// instead of waiting on the next 30s poll.

import { queryClient } from "./queryClient";
import { notificationStream, type StreamNotification } from "./notificationStream";
import { ANNOUNCEMENTS_QUERY_KEY } from "@/api/announcements";
import { DOCUMENTS_QUERY_KEY } from "@/api/files";
import { NOTIFICATIONS_QUERY_KEY } from "@/api/notifications";
import type { NotificationItem, UnreadCount } from "@/types/notification";

function onNotification(notification: StreamNotification) {
  const item = notification as unknown as NotificationItem;

  queryClient.setQueryData<NotificationItem[]>(
    [NOTIFICATIONS_QUERY_KEY, "list"],
    (previous) => {
      if (!previous) return previous; // nothing cached yet -- the next REST fetch will seed it
      if (previous.some((n) => n.id === item.id)) return previous; // reconnect replay guard
      return [item, ...previous].slice(0, 50); // mirrors the backend's own inbox cap
    }
  );

  if (!item.is_read) {
    queryClient.setQueryData<UnreadCount>(
      [NOTIFICATIONS_QUERY_KEY, "unread-count"],
      (previous) => (previous ? { unread: previous.unread + 1 } : previous)
    );
  }

  // The event payload only carries bell-shaped fields (title, body, kind) --
  // not an announcement's priority/buildings/pinned state -- so the
  // announcement lists refetch rather than being patched in place.
  if (item.kind === "ANNOUNCEMENT_PUBLISHED") {
    void queryClient.invalidateQueries({ queryKey: [ANNOUNCEMENTS_QUERY_KEY] });
  }
  if (item.kind === "DOCUMENT_UPLOADED") {
    // A bell click can lead to a documents page that is already mounted.
    // Refresh its list when the event arrives so the new file is there.
    void queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] });
  }
}

notificationStream.subscribe(onNotification);
